import { useCallback, useEffect, useState } from 'react';
import {
  AssignmentRow,
  EngagementRow,
} from '../../../contracts/DSEContracts';
import DSEAssignmentSearchService, {
  IDSEAssignmentSearchService,
} from '../../../services/DSEAssignmentSearch.service';
import FMDashboardService, {
  IFMDashboardService,
} from '../../../services/FMDashboard.service';
import FMOneProfileService, {
  IFMOneProfileService,
} from '../../../services/FMOneProfile.service';
import {
  mapAssignmentRows,
  mapEngagementDetails,
} from '../DSECapacityHours/dseMetrics';

export type DSECapacityDetailsSource =
  | 'assignments'
  | 'engagements'
  | 'primarySkill';

export type DSECapacityDetailsLoadStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'partial'
  | 'error';

export interface DSECapacityDetailsError {
  source: DSECapacityDetailsSource;
  message: string;
  cause: unknown;
}

export interface DSECapacityDetails {
  assignments: AssignmentRow[];
  engagements: EngagementRow[];
  staffPreference: string;
  loading: boolean;
  empty: boolean;
  status: DSECapacityDetailsLoadStatus;
  errors: DSECapacityDetailsError[];
  retry: () => void;
}

export interface UseDSECapacityDetailsOptions {
  assignmentSearchService?: IDSEAssignmentSearchService;
  dashboardService?: IFMDashboardService;
  oneProfileService?: IFMOneProfileService;
}

interface DSECapacityDetailsState {
  assignments: AssignmentRow[];
  engagements: EngagementRow[];
  staffPreference: string;
  loading: boolean;
  status: DSECapacityDetailsLoadStatus;
  errors: DSECapacityDetailsError[];
}

interface SourceResult<T> {
  value?: T;
  error?: DSECapacityDetailsError;
}

const sourceLabel = (source: DSECapacityDetailsSource): string => {
  switch (source) {
    case 'assignments':
      return 'Assignments';
    case 'engagements':
      return 'Engagements';
    default:
      return 'Primary skills';
  }
};

const errorDetail = (cause: unknown): string => {
  if (cause instanceof Error) {
    return cause.message;
  }
  if (typeof cause === 'string') {
    return cause;
  }
  return 'Unknown error';
};

const loadSource = async <T>(
  source: DSECapacityDetailsSource,
  load: () => Promise<T>
): Promise<SourceResult<T>> => {
  try {
    return { value: await load() };
  } catch (cause) {
    return {
      error: {
        source,
        message: `${sourceLabel(source)} could not be loaded: ${errorDetail(
          cause
        )}`,
        cause,
      },
    };
  }
};

const initialState = (
  alias: string
): DSECapacityDetailsState => ({
  assignments: [],
  engagements: [],
  staffPreference: '',
  loading: !!alias,
  status: alias ? 'loading' : 'idle',
  errors: [],
});

/** Load assignment search + primary skills in parallel, then engagements for found demand IDs. */
export function useDSECapacityDetails(
  alias: string,
  options: UseDSECapacityDetailsOptions = {}
): DSECapacityDetails {
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState<DSECapacityDetailsState>(() =>
    initialState(alias)
  );

  useEffect(() => {
    let cancelled = false;
    const normalizedAlias = (alias || '').trim().toLowerCase();

    if (!normalizedAlias) {
      setState(initialState(''));
      return;
    }

    setState(initialState(normalizedAlias));

    (async () => {
      const [assignmentResult, skillResult] = await Promise.all([
        loadSource('assignments', () =>
          (
            options.assignmentSearchService ??
            DSEAssignmentSearchService.getSingleton()
          ).searchAssignments(normalizedAlias)
        ),
        loadSource('primarySkill', () =>
          (
            options.oneProfileService ??
            FMOneProfileService.getSingleton()
          ).getResourceSkills(normalizedAlias)
        ),
      ]);

      if (cancelled) {
        return;
      }

      let assignments = mapAssignmentRows(
        assignmentResult.value?.documents ?? []
      );
      let engagements: EngagementRow[] = [];
      const errors: DSECapacityDetailsError[] = [];
      let attemptedSourceCount = 2;

      if (assignmentResult.error) {
        errors.push(assignmentResult.error);
      }
      if (skillResult.error) {
        errors.push(skillResult.error);
      }

      const demandSourceIds = Array.from(
        new Set(
          assignments
            .map((row) => row.demandSourceId)
            .filter((id) => !!id)
        )
      );

      if (demandSourceIds.length > 0) {
        attemptedSourceCount += 1;
        const engagementResult = await loadSource('engagements', () =>
          (
            options.dashboardService ??
            FMDashboardService.getSingleton()
          ).getEngagementDetailsForDSE(demandSourceIds)
        );
        if (cancelled) {
          return;
        }
        if (engagementResult.error) {
          errors.push(engagementResult.error);
        } else {
          const mapped = mapEngagementDetails(
            assignments,
            engagementResult.value ?? []
          );
          assignments = mapped.assignments;
          engagements = mapped.engagements;
        }
      }

      if (cancelled) {
        return;
      }

      const primarySkillNames =
        skillResult.value?.primarySkills
          ?.map((skill) => skill.skillName || '')
          .filter((name) => !!name) ?? [];
      const staffPreference = Array.from(
        new Set(primarySkillNames)
      ).join(', ');
      const status: DSECapacityDetailsLoadStatus =
        errors.length === 0
          ? 'success'
          : errors.length >= attemptedSourceCount
          ? 'error'
          : 'partial';

      setState({
        assignments,
        engagements,
        staffPreference,
        loading: false,
        status,
        errors,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    alias,
    options.assignmentSearchService,
    options.dashboardService,
    options.oneProfileService,
    retryToken,
  ]);

  const retry = useCallback(() => setRetryToken((token) => token + 1), []);

  return {
    ...state,
    empty:
      !state.loading &&
      state.assignments.length === 0 &&
      state.engagements.length === 0,
    retry,
  };
}
